import { XPath } from '../../controls/xpath';
import { Checkbox } from '../../controls/checkbox';
import { JsonPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, OnInit, signal, viewChild } from '@angular/core';
import { FormsModule, NgForm, Validators } from '@angular/forms';
import { Layouts } from '../../controls/layouts';
import { Buttons } from '../../controls/buttons';
import { Datetimebox } from '../../controls/datetimebox';
import { Password, PasswordComponentValue } from '../../controls/password';
import { CalculateValidationTextFn } from '../../controls/components/internal/control-supporting-text/control-supporting-text.component';
import { ConditionalValidatorDirective } from '../../controls/directives/conditional-validator.directive';
import { Textbox } from '../../controls/textbox';
import { Selectbox } from '../../controls/selectbox';

type TemplateFormData = {
    name: string | null;
    email: string | null;
    zip: string | null;
    city: string | null;
    passwordHash: string | null;
    passwordSecret: PasswordComponentValue;
    xpath: string | null;
    backgroundColor: string | null;
    foregroundColor: string | null;
    moreColors: string[] | null;
    otherColors: string[] | null;
    receiveNewsletter: boolean | null;
    datetime: Date | null;
    date: Date | null;
    time: Date | null;
    whatever: string | null;
};

@Component({
    selector: 'app-control-test-template-forms',
    templateUrl: './control-test-template-forms.component.html',
    styleUrl: './control-test-template-forms.component.scss',
    imports: [FormsModule, JsonPipe, Layouts, Buttons, Datetimebox, Textbox, Selectbox, ConditionalValidatorDirective, XPath, Checkbox, Password],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ControlTestTemplateFormsComponent implements OnInit, AfterViewInit {
    private readonly templateForm = viewChild.required<NgForm>('tForm');

    readonly myData = signal(this.createInitialData());

    readonly colors = signal<Array<{ id: string; name: string; }> | null>(null);

    ngOnInit(): void {
        this.refresh();
    }

    ngAfterViewInit(): void {
        this.templateForm().form.disable();
    }

    refresh(): void {
        this.colors.set([
            { id: 'ba', name: 'Black' },
            { id: 'bu', name: 'Blue' },
            { id: 'ge', name: 'Green' },
            { id: 'cy', name: 'Cyan' },
            { id: 're', name: 'Red' },
            { id: 'ma', name: 'Magenta' },
            { id: 'yl', name: 'Yellow' },
            { id: 'wi', name: 'White' },
        ]);
        this.myData.set(this.createInitialData());
    }

    readonly getValidationText: CalculateValidationTextFn = (control) =>
        control?.errors?.pattern ? 'Name can only contain letters' : null;

    readonly zipValidators = [Validators.required];
    readonly zipCondition = (): string | null => {
        const formValue = this.getTemplateFormValue();
        const result = formValue.zip ?? null;
        return result;
    };

    readonly newsletterValidators = [Validators.required];
    readonly newsletterCondition = (): boolean | null => {
        const formValue = this.getTemplateFormValue();
        const result = formValue.receiveNewsletter ?? null;
        return result;
    };

    templateFormEdit(): void {
        this.templateForm().form.enable();
    }

    templateFormSubmit(): void {
        const formValue = this.getTemplateFormValue();
        const currentData = this.myData();

        this.myData.set({
            ...currentData,
            ...formValue,
            passwordSecret: formValue.passwordSecret ?? currentData.passwordSecret,
        });
        this.templateForm().form.disable();
    }

    templateFormCancel(): void {
        const templateForm = this.templateForm();
        templateForm.reset(this.myData());
        templateForm.form.markAsUntouched();
        templateForm.form.markAsPristine();
        templateForm.form.disable();
    }

    templateFormClear(): void {
        this.templateForm().form.reset();
    }

    updateValidation(components: Array<{ ngControl?: { control?: { updateValueAndValidity: () => void; } | null; }; }>): void {
        components.forEach((component) => {
            component.ngControl?.control?.updateValueAndValidity();
        });
    }

    private createInitialData(): TemplateFormData {
        return {
            name: 'John Doe',
            email: 'john@example.com',
            zip: '7100',
            city: null,
            passwordHash: null,
            passwordSecret: { id: '001c1d1fa3624f30b4491b4315cfa383', value: null } as PasswordComponentValue,
            xpath: "/*[local-name()='custom-test-xml']/*[local-name()='whatever-id']",
            backgroundColor: null,
            foregroundColor: 'red',
            moreColors: null,
            otherColors: ['bu', 'ge', 're'],
            receiveNewsletter: true,
            datetime: new Date('2024-05-02T09:42:37Z'),
            date: new Date(1714600800000), // 2024-05-02T00:00:00+02:00
            time: new Date(new Date().setHours(11, 37, 42)), // Today at 11:37:42 local time
            whatever: '<init>',
        };
    }

    private getTemplateFormValue(): Partial<TemplateFormData> {
        // NgForm values are untyped, so we narrow once and use typed access elsewhere.
        const formValue = this.templateForm().form.getRawValue() as Partial<TemplateFormData>;
        return formValue;
    }
}
